import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, get, post, postForm, withQuery } from "./client";
import { CUSTOMER_ORDERS_KEY } from "./customerOrders";

/** `/api/v1/customer-orders/read` and friends, as `prv.web.api.city_orders`
 * shapes them: the City's PO read from its PDF and checked against our BOQ. */

export type Keep = "city" | "ours";
export type LineState = "matched" | "differs" | "city_only" | "ours_only";
export type HoldCode =
  | "NO_BOQ"
  | "WRONG_CONTRACT"
  | "ALREADY_READ"
  | "ORDER_CLOSED"
  | "ORDER_ON_OTHER_BOQ"
  | "BOQ_NOT_ISSUED"
  | "BOQ_TAKEN"
  | "SEVERAL_WORK_ORDERS"
  | "MISREAD";

export interface CityLine {
  code: string;
  description: string;
  state: LineState;
  fields: ("qty" | "rate" | "amount")[];
  /** False for a code the City ordered that is not on our rate card. */
  can_use_city: boolean;
  message: string;
  city: {
    qty: string;
    uom: string;
    /** Null when the City priced the code at different rates; see `rates`. */
    rate: string | null;
    rates: string[];
    amount: string;
  } | null;
  ours: { qty: string; unit: string | null; rate: string | null; amount: string; stated: boolean } | null;
}

export interface Hold {
  code: HoldCode;
  message: string;
  overridable: boolean;
  order_id: number | null;
}

export interface SavedDecision {
  code: string;
  keep: Keep;
  reason: string | null;
}

export interface CityOrderReview {
  order: {
    number: string;
    po_date: string | null;
    contract_no: string;
    our_reference: string;
    work_order: string;
    requisition: string;
    excl_vat: string;
    vat: string;
    total_incl_vat: string | null;
    items: { item_no: string; description: string; total: string; vat: string }[];
  };
  boq: {
    id: number;
    name: string;
    status: string;
    quotation_id: number | null;
    site: { id: number; name: string } | null;
  } | null;
  choices: { id: number; name: string; status: string }[];
  existing: { id: number; status: string } | null;
  holds: Hold[];
  lines: CityLine[];
  warnings: string[];
  totals: { city_excl_vat: string; ours_excl_vat: string };
  fingerprint: string;
  /** Only when an order on file was checked again from its stored PDF. */
  order_id?: number;
  decisions?: SavedDecision[];
}

export interface CheckBody {
  boq_id: number | null;
  decisions: SavedDecision[];
  overridden: boolean;
  override_reason: string | null;
  confirm: boolean;
  fingerprint: string | null;
}

export interface Saved {
  order_id: number;
  status: string;
}

export function readCityOrder(file: File, boqId: number | null): Promise<CityOrderReview> {
  const form = new FormData();
  form.append("document", file, file.name);
  if (boqId !== null) {
    form.append("boq_id", String(boqId));
  }
  return postForm<CityOrderReview>("/api/v1/customer-orders/read", form);
}

export function recheckCityOrder(orderId: number, boqId: number | null): Promise<CityOrderReview> {
  return get<CityOrderReview>(withQuery(`/api/v1/customer-orders/${orderId}/check`, { boq_id: boqId }));
}

export interface SaveVars {
  /** The order on file being checked again, or null for a newly dropped PDF. */
  orderId: number | null;
  body: CheckBody;
  /** The dropped PDF, base64, for a new order. */
  attachment: { base64: string; filename: string } | null;
}

function useAfterSave() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: CUSTOMER_ORDERS_KEY });
    void client.invalidateQueries({ queryKey: ["boq"] });
    // The job's City PO, BOQ and invoice steps all follow the order.
    void client.invalidateQueries({ queryKey: ["quotations"] });
  };
}

export function useSaveCityOrder() {
  const after = useAfterSave();
  return useMutation<Saved, ApiError, SaveVars>({
    mutationFn: ({ orderId, body, attachment }) =>
      orderId !== null
        ? post<Saved>(`/api/v1/customer-orders/${orderId}/check`, body)
        : post<Saved>("/api/v1/customer-orders/read/save", {
            ...body,
            attachment_base64: attachment?.base64 ?? "",
            filename: attachment?.filename ?? "order.pdf",
          }),
    onSuccess: after,
  });
}

export function useRejectCityOrder(orderId: number) {
  const after = useAfterSave();
  return useMutation<Saved, ApiError, string>({
    mutationFn: (reason) => post<Saved>(`/api/v1/customer-orders/${orderId}/reject`, { reason }),
    onSuccess: after,
  });
}
